import { paperClasses } from '@mui/material';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { AddOn } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AddOnForm, { AddOnFormHandler } from './AddOnForm';

interface AddOnDialogProps {
  addOn?: AddOn;
  isOpen: boolean;
  onBusyChange?: (isBusy: boolean) => void;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddOnDialog = observer(
  ({ addOn, isOpen, onBusyChange, onClose, onSuccess }: AddOnDialogProps) => {
    const { toasterStore, userStore } = useStores();
    const { createAddOnByCompanyId, isCreating, isUpdating, updateAddOn } = useAddOns();
    const dialogRef = useRef<AddOnFormHandler>({});
    const companyId = getEffectiveUserCompanyId(userStore);

    const isCreatingOrUpdating = isCreating || isUpdating;
    const isEditingAddOn = Boolean(addOn?.id);

    useEffect(() => {
      onBusyChange?.(isCreatingOrUpdating);
    }, [isCreatingOrUpdating, onBusyChange]);

    const handleClose = () => {
      if (isCreatingOrUpdating) {
        return;
      }

      onClose();
    };

    const handleAddOnFormSubmit = async () => {
      const data = await dialogRef.current?.onSubmit?.();

      if (!data) {
        return;
      }

      let alertMessage: string;

      if (isEditingAddOn) {
        await updateAddOn(companyId, String(data.id), data);
        alertMessage = t('administration.rates.add_ons.add_on_updated');
      } else {
        await createAddOnByCompanyId(companyId, data);
        alertMessage = t('administration.rates.add_ons.add_on_created');
      }

      toasterStore.push(alert(alertMessage, AlertTypes.success));
      onSuccess?.();
      onClose();
    };

    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        preventCloseOnBackdropClick
        sx={{ [`.${paperClasses.root}`]: { maxWidth: '700px' } }}
      >
        <DialogHeader onClose={handleClose}>
          {isEditingAddOn
            ? t('administration.rates.add_ons.update_add_on')
            : t('administration.rates.add_ons.create_add_on')}
        </DialogHeader>

        <DialogContent>
          <AddOnForm ref={dialogRef} addOn={addOn} sx={{ mt: 0 }} />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isCreatingOrUpdating}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={isCreatingOrUpdating}
            loading={isCreatingOrUpdating}
            onClick={handleAddOnFormSubmit}
          >
            {isEditingAddOn ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

export default AddOnDialog;
